
import React, { useEffect, useState } from 'react';
import Logo from './Logo';

interface PromoModalProps {
  onRegister: () => void;
  onClose: () => void;
}

const PromoModal: React.FC<PromoModalProps> = ({ onRegister, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Verifica se já viu a promo nos últimos 7 dias
    const lastSeen = localStorage.getItem('promo_pioneiros_seen');
    const now = new Date().getTime();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

    if (!lastSeen || (now - parseInt(lastSeen)) > SEVEN_DAYS) {
      const timer = setTimeout(() => setIsVisible(true), 1500); // Delay sutil para carregar o site antes
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('promo_pioneiros_seen', new Date().getTime().toString());
    setIsVisible(false);
    onClose();
  };

  const handleAction = () => {
    localStorage.setItem('promo_pioneiros_seen', new Date().getTime().toString());
    onRegister();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-brand-navy/95 backdrop-blur-xl animate-in fade-in duration-500" onClick={handleClose} />
      
      <div className="relative bg-white w-full max-w-2xl rounded-[3.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-500 border border-white/20">
        {/* Banner Superior com Logo */}
        <div className="bg-brand-navy p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-brand-orange/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
          <div className="flex justify-center mb-6">
            <Logo className="h-14" variant="light" />
          </div>
          <span className="text-brand-orange text-[10px] font-black uppercase tracking-[0.4em] mb-4 block animate-pulse">Oportunidade de Lançamento</span>
          <h2 className="text-3xl sm:text-5xl font-title font-black text-white uppercase italic tracking-tighter leading-none italic">
            Ganhe 6 Meses de <br/>
            <span className="text-brand-orange">Plano PRO Grátis</span>
          </h2>
        </div>

        <div className="p-10 sm:p-14">
          <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-10 text-center">Vantagens de ser um Lojista Pioneiro:</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-brand-orange/10 rounded-2xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
              </div>
              <div>
                <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest mb-1 leading-none">Destaque Máximo</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-relaxed">Sua loja sempre nas primeiras posições da cidade.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-brand-blue/10 rounded-2xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              </div>
              <div>
                <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest mb-1 leading-none">Selo Verificado</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-relaxed">Passe confiança imediata para novos clientes.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-brand-orange/10 rounded-2xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16m-7 6h7"/></svg>
              </div>
              <div>
                <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest mb-1 leading-none">Catálogo Ilimitado</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-relaxed">Poste todos os seus produtos sem restrições.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-brand-blue/10 rounded-2xl flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
              </div>
              <div>
                <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest mb-1 leading-none">Métricas Reais</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-relaxed">Saiba exatamente quantos cliques o seu Whats recebeu.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <button 
              onClick={handleAction}
              className="w-full bg-brand-orange text-white font-black py-7 rounded-3xl uppercase tracking-[0.2em] text-[11px] shadow-2xl shadow-brand-orange/30 hover:scale-[1.02] active:scale-95 transition-all"
            >
              Garantir meu Benefício Grátis
            </button>
            <button 
              onClick={handleClose}
              className="w-full text-gray-300 hover:text-brand-navy font-black text-[10px] uppercase tracking-widest py-3 transition-colors"
            >
              Sou Comprador / Explorar Vitrine
            </button>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 text-center border-t border-gray-100">
          <p className="text-[8px] font-black text-gray-300 uppercase tracking-widest leading-none">Oferta válida para os primeiros 50 cadastros de Guarulhos</p>
        </div>
      </div>
    </div>
  );
};

export default PromoModal;
